export interface ParsedNotificationValues<T> {
  readonly value: ReadonlyArray<T>
  readonly error: NotificationValidationErrorCode | null
}

export type NotificationValidationErrorCode =
  | 'voice_channels_required'
  | 'voice_channels_limit'
  | 'voice_channel_invalid'
  | 'voice_channel_duplicate'
  | 'teamspeak_channels_limit'
  | 'teamspeak_channel_invalid'
  | 'teamspeak_channel_duplicate'
  | 'offsets_required'
  | 'offset_invalid'
  | 'offset_duplicate'

// Discord snowflakes are 17-20 digits with no leading zero. The
// previous `/^\d{17,20}$/` accepted "0"-prefixed ids, which are never
// valid Discord channel ids and would round-trip through `Number()` as
// silently-truncated values.
const DISCORD_SNOWFLAKE = /^[1-9]\d{16,19}$/
const POSITIVE_DECIMAL_ID = /^[1-9]\d{0,19}$/
const MAX_UNSIGNED_ID = 18_446_744_073_709_551_615n
export const MAX_VOICE_CHANNELS = 16
export const MAX_TEAMSPEAK_CHANNELS = 16
const MAX_OFFSET_SECONDS = 3600

function tokens(value: string): ReadonlyArray<string> {
  return value
    .split(/[\s,]+/)
    .map((token) => token.trim())
    .filter((token) => token !== '')
}

export function parseVoiceChannelIds(
  value: string | ReadonlyArray<string>,
  required = true,
): ParsedNotificationValues<string> {
  const channelIds = typeof value === 'string'
    ? tokens(value)
    : value.map((channelId) => channelId.trim()).filter((channelId) => channelId !== '')
  if (required && channelIds.length === 0) {
    return { value: [], error: 'voice_channels_required' }
  }
  if (channelIds.length > MAX_VOICE_CHANNELS) {
    return {
      value: [],
      error: 'voice_channels_limit',
    }
  }
  if (channelIds.some((id) => !DISCORD_SNOWFLAKE.test(id) || id.startsWith('0'))) {
    return {
      value: [],
      error: 'voice_channel_invalid',
    }
  }
  if (new Set(channelIds).size !== channelIds.length) {
    return { value: [], error: 'voice_channel_duplicate' }
  }
  return { value: channelIds, error: null }
}

/** TeamSpeak channel IDs share the canonical positive u64 format, not Discord's snowflake length. */
export function parseTeamspeakChannelIds(
  value: ReadonlyArray<string>,
): ParsedNotificationValues<string> {
  const channelIds = value.map((id) => id.trim()).filter((id) => id !== '')
  if (channelIds.length > MAX_TEAMSPEAK_CHANNELS) {
    return { value: [], error: 'teamspeak_channels_limit' }
  }
  if (channelIds.some((id) => !isCanonicalPositiveU64(id))) {
    return { value: [], error: 'teamspeak_channel_invalid' }
  }
  if (new Set(channelIds).size !== channelIds.length) {
    return { value: [], error: 'teamspeak_channel_duplicate' }
  }
  return { value: channelIds, error: null }
}

export function parseNotificationOffsets(
  value: string,
): ParsedNotificationValues<number> {
  const rawOffsets = tokens(value)
  if (rawOffsets.length === 0) {
    return { value: [], error: 'offsets_required' }
  }
  const offsets = rawOffsets.map(Number)
  if (offsets.some((offset) =>
    !Number.isInteger(offset) || offset < 0 || offset > MAX_OFFSET_SECONDS
  )) {
    return {
      value: [],
      error: 'offset_invalid',
    }
  }
  if (new Set(offsets).size !== offsets.length) {
    return { value: [], error: 'offset_duplicate' }
  }
  return { value: offsets, error: null }
}

export function isDiscordSnowflake(value: string): boolean {
  return DISCORD_SNOWFLAKE.test(value) && !value.startsWith('0')
}

/**
 * Discord's built-in soundboard sounds use short IDs such as `"1"`, while
 * guild-created sounds use ordinary snowflakes. Both are unsigned 64-bit
 * decimal IDs and must stay strings so JavaScript never rounds them.
 */
export function isDiscordSoundboardSoundId(value: string): boolean {
  return isCanonicalPositiveU64(value)
}

function isCanonicalPositiveU64(value: string): boolean {
  return POSITIVE_DECIMAL_ID.test(value) && BigInt(value) <= MAX_UNSIGNED_ID
}
